import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Forms.css';

interface FormDownload {
  id: string;
  title: string;
  description: string;
  icon: string;
  route: string;
}

const Forms: React.FC = () => {
  const navigate = useNavigate();
  
  const forms: FormDownload[] = [
    {
      id: 'new-client',
      title: 'New Client Registration',
      description: 'Required for all new clients. Includes contact information, emergency contacts, and basic pet information.',
      icon: '📋',
      route: '/forms/new-client'
    },
    {
      id: 'dog-info',
      title: 'Dog Information Form',
      description: 'Detailed information about your dog including feeding schedule, medications, behavior traits, and special needs.',
      icon: '🐕',
      route: '/forms/dog-info'
    },
    {
      id: 'cat-info',
      title: 'Cat Information Form',
      description: 'Complete profile for your feline friend including diet, litter preferences, medical needs, and personality.',
      icon: '🐈',
      route: '/forms/cat-info'
    },
    {
      id: 'medical-auth',
      title: 'Medical Authorization',
      description: 'Authorization for emergency veterinary care. Required for all pets staying with us.',
      icon: '🏥',
      route: '/forms/medical-auth'
    },
    {
      id: 'vaccination-record',
      title: 'Vaccination Record',
      description: 'Please provide a copy of your pet\'s current vaccination records from your veterinarian.',
      icon: '💉',
      route: '/forms/vaccination'
    },
    {
      id: 'service-agreement',
      title: 'Service Agreement',
      description: 'Terms of service agreement covering policies, cancellations, and liability waivers.',
      icon: '📝',
      route: '/forms/service-agreement'
    }
  ];

  const handleOpenForm = (form: FormDownload) => {
    navigate(form.route);
  };

  return (
    <div className="forms-page">
      <section className="forms-hero">
        <h1>Pet Care Forms</h1>
        <p>Download and complete these forms before your pet's stay</p>
      </section>

      <section className="forms-intro">
        <div className="container">
          <div className="intro-content">
            <h2>📄 Preparing for Your Pet's Visit</h2>
            <p>
              To ensure the best care for your furry family member, please download and complete 
              the appropriate forms before your pet's stay. These forms help me understand your 
              pet's needs, routines, and any special requirements.
            </p>
            <div className="intro-steps">
              <div className="step">
                <span className="step-number">1</span>
                <p>Download the required forms below</p>
              </div>
              <div className="step">
                <span className="step-number">2</span>
                <p>Fill out all information completely</p>
              </div>
              <div className="step">
                <span className="step-number">3</span>
                <p>Bring completed forms to drop-off or email them ahead</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="forms-grid-section">
        <div className="container">
          <h2>Available Forms</h2>
          <div className="forms-grid">
            {forms.map((form) => (
              <div key={form.id} className="form-card">
                <span className="form-icon">{form.icon}</span>
                <h3>{form.title}</h3>
                <p>{form.description}</p>
                <button 
                  className="download-btn"
                  onClick={() => handleOpenForm(form)}
                >
                  <span className="download-icon">📝</span>
                  Open Form
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="requirements-section">
        <div className="container">
          <h2>What to Bring</h2>
          <div className="requirements-grid">
            <div className="requirement-card required">
              <div className="card-header">
                <span className="badge">Required</span>
                <h3>Essential Items</h3>
              </div>
              <ul>
                <li>Completed registration and pet info forms</li>
                <li>Signed service agreement</li>
                <li>Medical authorization form</li>
                <li>Copy of vaccination records</li>
                <li>Your pet's regular food (enough for their stay + extra)</li>
                <li>Any medications with clear instructions</li>
              </ul>
            </div>
            
            <div className="requirement-card recommended">
              <div className="card-header">
                <span className="badge">Recommended</span>
                <h3>Comfort Items</h3>
              </div>
              <ul>
                <li>Your pet's favorite bed or blanket</li>
                <li>Favorite toys</li>
                <li>Treats (with feeding instructions)</li>
                <li>Collar with ID tags</li>
                <li>Leash and harness (for dogs)</li>
                <li>Any comfort items from home</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="vaccination-section">
        <div className="container">
          <h2>Vaccination Requirements</h2>
          <p className="section-subtitle">All pets must be up-to-date on the following vaccinations</p>
          <div className="vaccination-grid">
            <div className="vaccination-card">
              <span className="pet-icon">🐕</span>
              <h3>Dogs</h3>
              <ul>
                <li><strong>Rabies</strong> - Current</li>
                <li><strong>DHPP</strong> - Distemper, Hepatitis, Parvo, Parainfluenza</li>
                <li><strong>Bordetella</strong> - (Kennel Cough) Within 6 months</li>
                <li><strong>Canine Influenza</strong> - Recommended</li>
              </ul>
            </div>
            <div className="vaccination-card">
              <span className="pet-icon">🐈</span>
              <h3>Cats</h3>
              <ul>
                <li><strong>Rabies</strong> - Current</li>
                <li><strong>FVRCP</strong> - Feline Distemper</li>
                <li><strong>FeLV</strong> - Feline Leukemia (if outdoors)</li>
              </ul>
            </div>
          </div>
          <p className="vaccination-note">
            💡 Please bring a copy of your pet's vaccination records from your veterinarian. 
            Records can be emailed to us or brought at drop-off.
          </p>
        </div>
      </section>

      <section className="contact-cta">
        <div className="container">
          <h2>Questions About Forms?</h2>
          <p>If you have any questions about the forms or what to bring, don't hesitate to reach out!</p>
          <div className="contact-buttons">
            <a href="mailto:thepetnannyofweston@gmail.com" className="btn btn-primary">
              📧 Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Forms;
